// Small building blocks shared by the step renderers: prompts, choice cards, the answer reveal.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { L } from '@/prakrit/lib/lessons/types'
import type { ClientOption, ClientStep } from '@/prakrit/lib/lessons/resolve'
import type { GradeResponse, Reveal } from '@/prakrit/components/lesson/lesson-types'

import { Check, Lightbulb, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefs } from '@/lib/client/prefs'
import { useReducedMotion } from '@/hooks/use-media-query'
import { Bi } from '@/prakrit/components/common/bi'
import { toneOf } from '@/prakrit/components/lesson/lesson-types'

/**
 * Fewer animations: "fewer" in Preferences, or the device's reduced-motion setting unless Preferences say "all".
 * False on the server and the first render (the device setting is read after mount).
 */
export const useFewerAnimations = (): boolean => {
    const { motion } = usePrefs()
    const system = useReducedMotion()

    return motion === 'reduce' || (motion !== 'full' && system)
}

/** The motion library's reduced-motion setting that follows Preferences, as in the Sanskrit course. */
export const useMotionSetting = (): 'always' | 'never' | 'user' => {
    const { motion } = usePrefs()

    return motion === 'reduce' ? 'always' : motion === 'full' ? 'never' : 'user'
}

export const StepPrompt: FC<{ text: L, className?: string }> = ({ text, className }): ReactNode => (
    <h2 className={cn('flex flex-col gap-0.5 font-sans', className)}>
        <span lang='hi' data-ex='hi' className='hindi text-xl font-semibold leading-snug sm:text-2xl'>{text.hi}</span>
        <span lang='en' data-ex='en' className='text-base font-normal text-muted-foreground'>{text.en}</span>
    </h2>
)

interface PillButtonProps {
    icon: LucideIcon
    hi: string
    en: string
    onClick: () => void
    pressed?: boolean
    className?: string
}

export const PillButton: FC<PillButtonProps> = ({ icon: Icon, hi, en, onClick, pressed, className }): ReactNode => (
    <button
        type='button'
        onClick={onClick}
        aria-pressed={pressed}
        className={cn(
            'inline-flex min-h-tap items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium transition-colors hover:border-saffron/60 hover:bg-saffron/5',
            pressed && 'border-saffron bg-saffron/10',
            className
        )}
    >
        <Icon className='size-4 text-saffron-strong' aria-hidden='true' />
        <span lang='hi' className='hindi'>{hi}</span>
        <span aria-hidden='true' className='text-muted-foreground/70'>·</span>
        <span lang='en' className='text-muted-foreground'>{en}</span>
    </button>
)

export const StepNote: FC<{ text: L, className?: string }> = ({ text, className }): ReactNode => (
    <p className={cn('flex items-start gap-2.5 rounded-xl bg-surface-muted px-4 py-3 text-left', className)}>
        <Lightbulb className='mt-1 size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
        <Bi hi={text.hi} en={text.en} hiClassName='leading-relaxed' enClassName='text-sm' />
    </p>
)

const norm = (value: string): string => value
    .normalize('NFC')
    .toLowerCase()
    .replace(/[।॥?!.,;:"'“”‘’()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Would showing the Hindi/English meaning under the Prakrit options give the answer away?
 * (e.g. "Which one means 'I read'?" with "अहं पढामि। — I read." as an option). Then meanings wait until it is answered.
 */
export const meaningsGiveAway = (prompts: (L | undefined)[], options: ClientOption[]): boolean => {
    const haystack = ` ${prompts.filter(Boolean).map(p => `${norm((p as L).hi)} ${norm((p as L).en)}`).join(' ')} `

    return options.some(o => o.pra && [o.hi, o.en].some(m => {
        const needle = norm(m)

        return needle.length >= 2 && haystack.includes(` ${needle} `)
    }))
}

/** Arrow keys move focus between the cards of a choice group (Space selects, Enter checks). */
export const moveBetween = (event: KeyboardEvent<HTMLElement>, selector = '[data-option]') => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return

    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(selector))
    const index = items.indexOf(document.activeElement as HTMLElement)

    if (index < 0) return

    event.preventDefault()

    const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1

    items[(index + delta + items.length) % items.length]?.focus()
}

type CardState = 'idle' | 'selected' | 'correct' | 'wrong' | 'reveal'

const CARD: Record<CardState, string> = {
    idle: 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5',
    selected: 'border-saffron bg-saffron/10',
    correct: 'border-success bg-success-soft',
    wrong: 'animate-shake-x border-danger bg-danger-soft',
    reveal: 'border-dashed border-success bg-card'
}

interface OptionListProps {
    options: ClientOption[]
    value: string | null
    onChange: (id: string) => void
    locked: boolean
    result: GradeResponse | null
    revealId?: string | null
    /** Show Hindi/English under Prakrit options. */
    showMeaning: boolean
    label: string
}

export const OptionList: FC<OptionListProps> = ({ options, value, onChange, locked, result, revealId = null, showMeaning, label }): ReactNode => {
    const tone = toneOf(result)

    return (
        <div role='radiogroup' aria-label={label} onKeyDown={event => moveBetween(event)} className='flex flex-col gap-2.5'>
            {options.map((option, i) => {
                const selected = value === option.id
                let state: CardState = selected ? 'selected' : 'idle'

                if (selected && tone === 'correct') state = 'correct'
                else if (selected && tone === 'wrong') state = 'wrong'
                else if (revealId === option.id) state = 'reveal'

                return (
                    <button
                        key={option.id}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        aria-disabled={locked || undefined}
                        data-option
                        data-enter-submit
                        onClick={() => {
                            if (!locked) onChange(option.id)
                        }}
                        className={cn(
                            'flex min-h-tap w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left shadow-sm transition-colors',
                            CARD[state],
                            locked && state === 'idle' && 'opacity-60 hover:border-border hover:bg-card'
                        )}
                    >
                        <span
                            aria-hidden='true'
                            className={cn(
                                'flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold',
                                state === 'selected' ? 'border-saffron bg-saffron text-foreground' : state === 'correct' ? 'border-success bg-success text-white dark:text-background' : state === 'wrong' ? 'border-danger bg-danger text-white dark:text-background' : 'border-border bg-background text-muted-foreground'
                            )}
                        >
                            {state === 'correct' || state === 'reveal' ? <Check className='size-4' /> : state === 'wrong' ? <X className='size-4' /> : i + 1}
                        </span>
                        <span className='flex min-w-0 flex-1 flex-col gap-0.5 break-words'>
                            {option.pra ? (
                                <>
                                    <span lang='pra' className='pra pra-lg'>{option.pra}</span>
                                    {showMeaning && (
                                        <Bi hi={option.hi} en={option.en} layout='inline' className='text-sm' enClassName='text-muted-foreground' />
                                    )}
                                </>
                            ) : (
                                <Bi hi={option.hi} en={option.en} hiClassName='text-lg font-medium' enClassName='text-sm' />
                            )}
                        </span>
                        {state === 'reveal' && (
                            <span className='shrink-0 text-xs font-semibold text-success'>
                                <span lang='hi' className='hindi'>सही उत्तर</span>
                                <span className='sr-only'> · correct answer</span>
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}

/** The option whose text the server gave back as the expected answer. */
export const optionMatching = (options: ClientOption[], expected: string | undefined): string | null => {
    if (!expected) return null

    const hit = options.find(o => (o.pra ? o.pra === expected : `${o.hi} · ${o.en}` === expected))

    return hit?.id ?? null
}

const isPrakrit = (text: string): boolean => /[ऀ-ॿ]/.test(text) && !text.includes(' · ')

/** The revealed answer as one line of text, for the screen-reader announcement beside the feedback. */
export const revealText = (reveal: Reveal | null, step: ClientStep | undefined): { text: string, pra: boolean } | null => {
    if (!reveal || !step) return null

    if (reveal.expectedPairs && step.type === 'match') {
        const lefts = step.left
        const rights = step.right
        const text = Object.entries(reveal.expectedPairs).map(([l, r]) => {
            const left = lefts.find(o => o.id === l)
            const right = rights.find(o => o.id === r)

            return `${left?.pra ?? left?.hi ?? ''} – ${right?.pra ?? right?.hi ?? ''}`
        }).join('; ')

        return text ? { text, pra: false } : null
    }

    return reveal.expected ? { text: reveal.expected, pra: isPrakrit(reveal.expected) } : null
}

/** The answer, shown after two wrong tries (learn / practice) or once a test question has been answered. */
export const RevealCard: FC<{ reveal: Reveal, step: ClientStep }> = ({ reveal, step }): ReactNode => {
    const pairs = reveal.expectedPairs && step.type === 'match'
        ? Object.entries(reveal.expectedPairs).map(([l, r]) => ({
            left: step.left.find(o => o.id === l),
            right: step.right.find(o => o.id === r)
        }))
        : null

    if (!pairs && !reveal.expected) return null

    return (
        <aside className='animate-pop-in rounded-xl border border-dashed border-success/60 bg-success-soft/60 px-4 py-3'>
            <p className='mb-1.5 text-sm font-semibold text-foreground'>
                <Bi hi='सही उत्तर' en='The answer' layout='inline' always />
            </p>
            {pairs ? (
                <ul className='flex flex-col gap-1'>
                    {pairs.map((p, i) => (
                        <li key={i} className='flex flex-wrap items-baseline gap-x-2'>
                            <span lang={p.left?.pra ? 'pra' : undefined} className={cn(p.left?.pra ? 'pra pra-md' : 'font-medium')}>{p.left?.pra ?? p.left?.hi}</span>
                            <span aria-hidden='true' className='text-muted-foreground'>→</span>
                            <span lang={p.right?.pra ? 'pra' : undefined} className={cn(p.right?.pra ? 'pra pra-md' : 'font-medium')}>{p.right?.pra ?? p.right?.hi}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p lang={isPrakrit(reveal.expected as string) ? 'pra' : undefined} className={cn(isPrakrit(reveal.expected as string) ? 'pra pra-lg' : 'text-lg font-medium')}>
                    {reveal.expected}
                </p>
            )}
        </aside>
    )
}
